import { AgentConfig } from "../types";

export const ORDER_TRACKING_AGENT: AgentConfig = {
  slug: "order-tracking",
  name: "Order Tracking Agent",
  vertical: "e-commerce",
  description: "Handles order status, cancellations, in-store support, and product recommendations end-to-end.",
  personaName: "Anna",
  personaVoiceId: "anna-ecomm-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  did: "+1 (555) 010-0000",
  isRecommended: true,
  // Live crew (US, Shopify workspace e3d23869): Orchestration → WISMO / Store Pickup / Price Objection → Human Transfer.
  agentId: process.env.REACT_APP_ELECTRONICS_AGENT_ID ?? "a94e6722-0635-4ed8-87d6-3a56ab778035",
  workspaceId: process.env.REACT_APP_ELECTRONICS_WORKSPACE_ID ?? "e3d23869-60c0-4191-8852-08e67de83a39",
  agentNameOverrides: {
    "4dd5dff8-dd73-40bf-87f0-d5b81de8d989": "Orchestration",
    "8d6a338f-7112-4392-b837-e8255c45843d": "WISMO",
    "905beb0c-1d5d-43bd-9cee-94eb0b89bee5": "Store Pickup",
    "d546b558-ed29-4cc8-8146-06b126e0648a": "Price Objection",
    "c740da4e-44df-4f56-a4c5-8418ff9c1427": "Human Transfer",
  },
  brandName: "NuPlay Electronics",
  brandTagline: "Customer Support",
  storeUrl: "https://nurix-electronics-2.myshopify.com",
  seedContext: {
    customerName: "Tony",
    customerEmail: "tony@gmail.com",
    items: [
      { id: "ORD-8821", label: "UltraSound Wireless Earbuds", status: "Delayed" },
      { id: "ORD-8756", label: "Mechanical Keyboard Pro", status: "Delivered" },
      { id: "ORD-8799", label: "USB-C Hub 7-in-1", status: "Cancelled" },
    ],
  },
  activities: ["Look up order", "Check shipment", "Process cancellation", "Recommend products"],
  sampleUtterances: [
    "Where is my UltraSound Wireless Earbuds order?",
    "I want to cancel my USB-C Hub order.",
    "Is this product available at my nearest store?",
    "What headphones would you recommend for working from home?",
  ],
  queries: [
    {
      id: "wismo",
      label: "Where Is My Order?",
      description: "Track delivery status and get live shipment updates for recent orders.",
      sampleUtterances: [
        "Where is my UltraSound Wireless Earbuds order?",
        "My order was supposed to arrive yesterday — what happened?",
        "Can you give me the tracking number for ORD-8821?",
        "Is there any delay on my recent shipment?",
      ],
    },
    {
      id: "cancellation",
      label: "Cancel or Modify",
      description: "Cancel an order, request a change, or initiate a return or refund.",
      sampleUtterances: [
        "I want to cancel my USB-C Hub order.",
        "Can I change the delivery address for ORD-8821?",
        "I'd like a refund on the keyboard I bought last week.",
        "Is it too late to cancel ORD-8799?",
      ],
    },
    {
      id: "store-desk",
      label: "Store Desk Support",
      description: "Check in-store availability, store hours, and get help with in-person purchases.",
      sampleUtterances: [
        "Is the UltraSound Earbuds available at my nearest store?",
        "What are the store hours for your downtown location?",
        "I bought something in-store and the receipt was wrong — can you help?",
        "Do you have the keyboard in stock at the SF store?",
      ],
    },
    {
      id: "product-rec",
      label: "Product Recommendation",
      description: "Get personalised suggestions based on your purchase history and preferences.",
      sampleUtterances: [
        "What wireless headphones would you recommend for working from home?",
        "I loved the keyboard I bought — what accessories pair well with it?",
        "I'm looking for a USB hub that works with MacBook Pro.",
        "What's a good gift for someone who works in tech?",
      ],
    },
  ],
};

// ─── Copa Airlines crew (live 4-agent squad on the Copa workspace) ──────────────

export const COPA_CREW_AGENT: AgentConfig = {
  slug: "copa-airlines",
  name: "Copa Airlines Support",
  vertical: "airline",
  description:
    "Compassionate refund status + rebooking handoff and delayed-baggage reimbursement + voucher status, routed across a 4-agent crew.",
  personaName: "Pedro",
  personaVoiceId: "copa-crew-v1",
  avatarUrl: "/avatars/alice.jpg",
  supportedChannels: ["web"],
  isRecommended: true,
  // Live COPA Crew: routing agent → refund / baggage assistants → human transfer.
  // Lives in a different workspace than the Shopify agent, on the same agentx-us backend.
  agentId: process.env.REACT_APP_COPA_AGENT_ID ?? "4c3a3587-bc59-4729-a255-592b4399ddb3",
  workspaceId: process.env.REACT_APP_COPA_WORKSPACE_ID ?? "37e42cba-f6ce-45d3-be2f-1075cce1ae96",
  agentNameOverrides: {
    "2d60623d-0a43-465d-bdca-5104abe8d0ca": "Routing Agent",
    "a8910103-3238-473f-a10d-aa0cb808ef96": "Refund & Rebooking Assistant",
    "026b09c4-9a4d-4dd6-8a56-ec08f0647422": "Baggage & Voucher Assistant",
    "450cc2be-53e6-45bc-aac3-e342a75db86b": "Human Transfer Agent",
  },
  seedContext: { customerName: "Passenger", items: [] },
  activities: ["Verify passenger", "Check refund status", "Check baggage & voucher", "Prepare specialist handoff"],
  sampleUtterances: [
    "I had to cancel my trip due to a bereavement — where is my refund?",
    "My bag arrived two days late and I'm waiting on a reimbursement.",
    "Can I use my refund toward a new ticket next month?",
    "I still haven't received my voucher.",
  ],
  queries: [
    {
      id: "refund-status",
      label: "Refund Status & Rebooking",
      description: "Check a refund case, explain the status with empathy, and hand off rebooking to a specialist.",
      sampleUtterances: [
        "I requested a refund after a bereavement — what's the status?",
        "Booking CP9M4L, last name Rodríguez — can you check my refund?",
        "Could I put the refund toward a new Panama City to New York trip?",
        "Is there a voucher option while the refund is under review?",
      ],
    },
    {
      id: "baggage-reimbursement",
      label: "Baggage Reimbursement & Voucher",
      description: "Check a delayed-baggage claim, reimbursement case, and voucher status, then escalate approval.",
      sampleUtterances: [
        "My bag was delayed on my Bogotá to Miami trip — where's my reimbursement?",
        "Booking CP7KQ2, last name Morales — what's my baggage claim status?",
        "I was told I'd get a voucher but haven't received it.",
        "Can you resend the voucher and the receipt upload link?",
      ],
    },
  ],
};

// ─── Tractor Supply "Riley" — TSC CX crew (live 4-agent V2 squad, TSC Demo Workspace) ─

export const TSC_CREW_AGENT: AgentConfig = {
  slug: "tsc",
  name: "Riley",
  vertical: "e-commerce",
  description:
    "Tractor Supply's virtual associate. Verifies the caller, cancels online orders by the cancellation SOP, saves the sale with one cheaper offer, resets passwords, and warm-transfers everything else to a teammate.",
  personaName: "Riley",
  personaVoiceId: "tsc-riley-v1",
  avatarUrl: "/tsc/riley.png",
  supportedChannels: ["web", "phone"],
  isRecommended: true,
  brandName: "Tractor Supply",
  brandTagline: "Customer Support",
  // "TSC CX Crew (23rd oct)": Orchestrator → Order Cancellation → Recovery and Upsell,
  // Orchestrator → Password Reset. Warm transfer is a tool on each agent (not a crew
  // member) and dials the page's warm-transfer number, falling back to the demo line.
  // Verification is the account email + billing zip. Calls run the PUBLISHED crew.
  agentId: process.env.REACT_APP_TSC_AGENT_ID ?? "b3fcf032-fccf-4ea0-be76-e5f5fb95dcbd",
  workspaceId: process.env.REACT_APP_TSC_WORKSPACE_ID ?? "02e5937e-e1fe-4a99-9769-8c2206609a48",
  agentNameOverrides: {
    "d65882da-6298-46bf-963e-e29e15ead03e": "Orchestrator",
    "8bdeb499-67b9-4d8d-9f2e-653645e15212": "Order Cancellation",
    "656278eb-52d1-4a8e-8688-62c6ae7bf22c": "Recovery and Upsell",
    "3a0cc261-8123-47b2-9953-920717f7c469": "Password Reset",
  },
  seedContext: { customerName: "Customer", items: [] },
  activities: ["Verify caller (email + billing zip)", "Cancel or request a cancellation", "Recover with one cheaper offer", "Reset a password", "Warm-transfer to a teammate"],
  sampleUtterances: [
    "Hi, I'd like to cancel an order.",
    "Hi, I forgot my password.",
    "I want to return a dog collar I bought.",
    "Let me speak to a supervisor.",
  ],
  queries: [
    {
      id: "cancellation",
      label: "Cancel an order",
      description: "Cancel inside 59 minutes on the call. After that, store, warehouse and vendor requests by the SOP, then one cheaper offer.",
      sampleUtterances: [
        "Hi, I'd like to cancel an order.",
        "I found it cheaper somewhere else.",
        "I still need it cancelled.",
        "Let me speak to a supervisor.",
      ],
    },
  ],
};

// ─── Tractor Supply "Mike" (clone of the TSC CX crew with a male voice) ─────────
// Same pages, customers and tasks as TSC_CREW_AGENT, picked with the Riley / Mike toggle on
// /tsc (tsc/tscDemos.ts). Its own crew in the same workspace; the clone's sub-agents have
// their own ids. Calls run the PUBLISHED crew. No photo yet: until public/tsc/mike.png
// exists the page shows "M" on TSC red.

export const TSC_MIKE_AGENT: AgentConfig = {
  ...TSC_CREW_AGENT,
  slug: "tsc-mike",
  name: "Mike",
  personaName: "Mike",
  personaVoiceId: "tsc-mike-v1",
  avatarUrl: "/tsc/mike.png",
  agentId: process.env.REACT_APP_TSC_MIKE_AGENT_ID ?? "5559c374-5c7d-4b4b-b8d2-66b07768be30",
  agentNameOverrides: {
    "1280da4f-6cb4-4ec8-967f-381f84584d0c": "Orchestrator",
    "c3a53d76-8996-40df-9b84-02754f3411ee": "Order Cancellation",
    "347193aa-2eba-496a-a4a8-e80156e7976c": "Recovery and Upsell",
    "fd5a4aee-e4ef-4e82-9dd5-5f0d8bc7639c": "Password Reset",
  },
};

// ─── Tractor Supply "Riley", multilingual (clone of the TSC CX crew, en / es / hi) ──
// A separate demo for other clients: same pages and customers as TSC_CREW_AGENT, its own
// crew (CLONE_TSC CX Crew, TSC Demo Workspace) and URL (/multilingual-support-tsc).
// Calls run the PUBLISHED crew; the clone's sub-agents have their own ids.

export const TSC_MULTILINGUAL_AGENT: AgentConfig = {
  ...TSC_CREW_AGENT,
  slug: "multilingual-support-agent",
  description:
    "Tractor Supply's virtual associate, in English, Spanish and Hindi. Verifies the caller, cancels online orders by the cancellation SOP, saves the sale with one cheaper offer, resets passwords, and warm-transfers everything else to a teammate.",
  agentId: process.env.REACT_APP_TSC_MULTILINGUAL_AGENT_ID ?? "de3fefc0-aee6-481b-9698-b9e97bd653a8",
  agentNameOverrides: {
    "6ae4c0e9-e81c-4370-9645-f1f4ba8467cf": "Orchestrator",
    "25171617-3787-45e6-9d88-20450de23097": "Order Cancellation",
    "2eada2a3-796e-4f88-9411-e5f9f855df0f": "Recovery and Upsell",
    "2039d894-e1b4-48cb-b51d-cf0f68c1b80e": "Password Reset",
  },
};

// ─── KnitWell / Ann Taylor WISMO agent (single agent, live) ─────────────────────

export const KNITWELL_AGENT: AgentConfig = {
  slug: "knitwell",
  name: "Ava — Ann Taylor",
  vertical: "e-commerce",
  description: "Ann Taylor's order-tracking (WISMO) assistant — verifies the caller, then looks up recent orders and tracks the shipment.",
  personaName: "Ava",
  personaVoiceId: "knitwell-ava-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  did: "+1 (833) 401-0599",
  isRecommended: true,
  brandName: "Ann Taylor",
  brandTagline: "Order Support",
  agentId: process.env.REACT_APP_KNITWELL_AGENT_ID ?? "8523c8d7-0569-4bdd-89f7-9df83bf37a19",
  workspaceId: process.env.REACT_APP_KNITWELL_WORKSPACE_ID ?? "f0c238dd-a379-4622-b7c1-0041b8988f69",
  seedContext: { customerName: "John Mike", items: [] },
  activities: ["Verify caller", "Look up recent orders", "Track shipment & ETA"],
  sampleUtterances: [
    "Where is my order?",
    "Has my order shipped yet?",
    "When will it arrive?",
    "Can you check my recent orders?",
  ],
  queries: [
    {
      id: "wismo",
      label: "Where Is My Order?",
      description: "Track delivery status and get live shipment updates across recent orders.",
      sampleUtterances: [
        "Where is my order?",
        "Has my order shipped yet?",
        "When will it arrive?",
        "Can you check the status of my recent orders?",
      ],
    },
  ],
};

// ─── Fintech "Claire" client-services agent (BFSI — B2B invoices & payments) ────

export const FINTECH_AGENT: AgentConfig = {
  slug: "fintech",
  name: "Claire — Fintech Client Services",
  vertical: "bfsi",
  description:
    "Fintech's B2B invoice and payments assistant — verifies the caller, pulls the account's service tier, then answers returned drafts, delivery holds, missing invoices and accounting-sync questions strictly to what that tier allows.",
  personaName: "Claire",
  personaVoiceId: "fintech-claire-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  isRecommended: true,
  brandName: "Fintech",
  brandTagline: "Client Services",
  // Claire — Client Services, a single inbound agent (no crew) on agentx-us. The
  // warm-transfer node is added separately; Gold and Silver escalation is handled
  // in-agent anyway, so only the Platinum "get me my account manager" beat needs it.
  agentId: process.env.REACT_APP_FINTECH_AGENT_ID ?? "c67588ab-0753-44b3-b115-f800cefb6cc8",
  workspaceId: process.env.REACT_APP_FINTECH_WORKSPACE_ID ?? "1daba44d-7014-4e1a-9f11-40f7d9af9f04",
  seedContext: { customerName: "Dana", items: [] },
  activities: ["Verify caller & pull service tier", "Open Salesforce case — priority by tier", "Email a branded call summary"],
  sampleUtterances: [
    "Our weekly draft came back and now the distributor won't deliver.",
    "There's a delivery hold on our account — can you release it?",
    "I signed for a delivery days ago and the invoice still isn't in the portal.",
    "Our invoices haven't synced into Sage Intacct for three days.",
  ],
  queries: [
    {
      id: "returned-payment",
      label: "Returned payment / failed draft",
      description: "A weekly draft came back unpaid — explain why, what it flipped back to unpaid, and what the tier allows on a re-draft and the returned-item fee.",
      sampleUtterances: [
        "Tuesday's draft for $38,412.65 came back and nine invoices flipped to unpaid.",
        "The bank returned it for insufficient funds — can you re-run it today?",
        "Can you waive the returned-item fee? This has never happened before.",
        "Two identical drafts for $12,304.18 hit us on the same day — I need one back.",
      ],
    },
    {
      id: "compliance-hold",
      label: "Delivery hold & state credit law",
      description: "A returned draft has put the account on a state delinquent list — explain the credit-law hold and exactly what can and cannot be promised to get the delivery released.",
      sampleUtterances: [
        "Republic refused my delivery this morning and I don't know why.",
        "Someone told me we're on the Louisiana delinquent list — is that right?",
        "The draft came back nine days ago and it still hasn't cleared.",
        "What do I have to pay, today, to get the truck to come back?",
      ],
    },
    {
      id: "missing-invoice",
      label: "Invoice missing from the portal",
      description: "A delivery was signed for but the invoice never transmitted — trace the missing document and set the right expectation for when it will appear.",
      sampleUtterances: [
        "I signed for a Reyes delivery four days ago and it's not in the portal.",
        "It was about $4,268.40 — I've got the signed sheet in front of me.",
        "Will it get picked up in this week's draft or the next one?",
        "The earlier Reyes invoice is there, so why isn't this one?",
      ],
    },
    {
      id: "integration-sync",
      label: "Accounting sync failure",
      description: "Invoice files have stopped landing in the client's accounting system — diagnose the sync, then commit to a fix window the tier actually supports.",
      sampleUtterances: [
        "Three days of invoices haven't come through into Sage Intacct.",
        "Our month-end close is Friday — I need these files today.",
        "Southern Glazer's and US Foods are both missing from the feed.",
        "Can someone from the integrations team call me back?",
      ],
    },
  ],
};

// ─── NuPlay Fashion (Mira — 4-agent care crew, live on agentx-us) ───────────────

export const FASHION_CREW_AGENT: AgentConfig = {
  slug: "nuplay-fashion",
  name: "NuPlay Fashion Care",
  vertical: "e-commerce",
  description:
    "Full customer-care crew for a premium apparel brand — orders and delivery, returns and complaints, styling and loyalty — where the loyalty tier decides what can be promised.",
  personaName: "Mira",
  personaVoiceId: "mira-fashion-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  isRecommended: true,
  brandName: "NuPlay Fashion",
  brandTagline: "Customer Care",
  agentId: process.env.REACT_APP_FASHION_AGENT_ID ?? "a923e881-04ca-46bf-98f4-5767705e559c",
  workspaceId: process.env.REACT_APP_FASHION_WORKSPACE_ID ?? "c0d49d44-eb9c-44fa-9729-6d45581b0188",
  agentNameOverrides: {
    "4f8d98da-0c37-4dad-96af-b3c6dd8c13b1": "Front Door",
    "e24d7b22-7857-4daa-8fc5-34ca8074deb6": "Orders & Delivery",
    "093b6b3c-4a5b-4d11-ac53-9bd1b7c5718e": "Returns & Care",
    "f66c340c-bb03-4d67-9b14-fa73e55a318a": "Styling & Close",
  },
  seedContext: { customerName: "Customer", items: [] },
  activities: ["Verify the customer", "Track an order", "Start a return", "Recommend and close"],
  sampleUtterances: [
    "Where is my order, it was supposed to be here already?",
    "I sent a dress back — where is my refund?",
    "I need a different size than the one I ordered.",
    "Is my store pickup order ready?",
  ],
  queries: [
    {
      id: "refund-status",
      label: "Refund & Returns",
      description: "Refund timelines, returns, exchanges and damaged or wrong items — the tier decides how fast the money moves.",
      sampleUtterances: [
        "Where is my money for the dress I sent back?",
        "I need a different size than what I ordered.",
        "The item arrived broken.",
        "You sent me the wrong shoes.",
      ],
    },
    {
      id: "wismo",
      label: "Orders & Delivery",
      description: "Order status, delays, cancellations with a save attempt, and store pickup.",
      sampleUtterances: [
        "Where is my order, it was supposed to be here already?",
        "My package is delayed and nobody told me why.",
        "I want to cancel my order.",
        "Is my store pickup order ready yet?",
      ],
    },
    {
      id: "product-rec",
      label: "Styling & Loyalty",
      description: "Outfit recommendations from purchase history, plus loyalty perks and early access.",
      sampleUtterances: [
        "What goes with the jacket I bought last month?",
        "What do I get with my membership?",
        "What size should I get, I am between sizes?",
        "Is the wrap dress back in stock in medium?",
      ],
    },
  ],
};

// ─── Nova Money (Meera — 5-desk consumer finance crew, live on agentx-us) ──────

export const NOVA_CREW_AGENT: AgentConfig = {
  slug: "nova-money",
  name: "Nova Money Consumer Care",
  vertical: "bfsi",
  description:
    "Consumer finance care across five desks — loans and EMIs, cards and disputes, KYC and payments — where the customer segment decides how fast money comes back.",
  personaName: "Meera",
  personaVoiceId: "meera-nova-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  isRecommended: true,
  brandName: "Nova Money",
  brandTagline: "Customer Care",
  agentId: process.env.REACT_APP_NOVA_AGENT_ID ?? "c215a6f2-dd92-4508-b4a3-77c9751688e1",
  workspaceId: process.env.REACT_APP_NOVA_WORKSPACE_ID ?? "f7855671-661e-422f-9a8a-4f0a06cdfc01",
  agentNameOverrides: {
    "58e83ab8-3eb7-4c95-b55d-52127cf984d7": "Front Door",
    "20cac86d-0356-4d0b-b9d3-b16a94a7a8de": "Loans & EMIs",
    "a25d4f1e-93b5-4d93-b8ca-ec13bb74329c": "Cards & Disputes",
    "80793c96-5a4b-4f05-9db4-9f7e57cdf3b6": "Payments & KYC",
    "57a22079-6ee7-4bc6-ba52-1dfa4c5bc805": "Resolution & Close",
  },
  seedContext: { customerName: "Customer", items: [] },
  activities: ["Verify the customer", "Check a loan or card", "Raise a dispute", "Log and close"],
  sampleUtterances: [
    "There is a charge on my card I do not recognise.",
    "I have lost my card — block it now.",
    "What would it cost to close my loan early?",
    "My UPI transfer failed and the money has gone.",
  ],
  queries: [
    {
      id: "returned-payment",
      label: "Cards & Disputes",
      description: "Unrecognised charges, lost or stolen cards, limit increases — the segment decides the credit timeline.",
      sampleUtterances: [
        "There is a charge on my card I do not recognise.",
        "I have lost my card, block it right now.",
        "Can you increase my credit limit?",
        "When will I get the money back?",
      ],
    },
    {
      id: "integration-sync",
      label: "Loans & EMIs",
      description: "Application status, EMI schedules, missed payments, prepayment and foreclosure quotes.",
      sampleUtterances: [
        "What would it cost to close my loan early?",
        "When is my next EMI and how much is it?",
        "My EMI bounced last month — what happens now?",
        "Where has my loan application got to?",
      ],
    },
    {
      id: "missing-invoice",
      label: "Payments & KYC",
      description: "Failed transfers, missing refunds, KYC re-verification and account updates.",
      sampleUtterances: [
        "My UPI transfer failed and the money has gone.",
        "A refund from a merchant has not arrived.",
        "The app says my KYC is pending.",
        "I need to change the address on my account.",
      ],
    },
  ],
};

// ─── Victoria's Secret (Ava — 6-agent retail crew, live VOICE_SQUAD_V2) ─────────

export const VS_CREW_AGENT: AgentConfig = {
  slug: "victorias-secret",
  name: "Victoria's Secret Customer Care",
  vertical: "e-commerce",
  description:
    "A full retail care crew for Victoria's Secret — order delivery and fulfilment recovery, returns and cancellations, gifting and sales — that verifies every anonymous caller in two steps before it says anything about the account.",
  personaName: "Ava",
  personaVoiceId: "ava-vs-v1",
  avatarUrl: "/agent-alice.png",
  supportedChannels: ["web", "phone"],
  isRecommended: true,
  brandName: "Victoria's Secret",
  brandTagline: "Customer Care",
  storeUrl: "https://www.victoriassecret.com",
  agentId: process.env.REACT_APP_VS_AGENT_ID ?? "975719b3-328e-4f1f-b9e4-46f644c30000",
  workspaceId: process.env.REACT_APP_VS_WORKSPACE_ID ?? "be26a7c9-011a-4f55-9866-fe9852ac1500",
  agentNameOverrides: {
    "91a6e615-f63c-44f3-b2ec-87234fbeaad0": "Front Door",
    "199c1b70-dc2b-4c9f-9d18-2488aad6a313": "Orders & Delivery",
    "9d2f35a8-833e-48b0-a3c3-23189505584c": "Fulfilment Recovery",
    "a101ef22-ea37-4c71-b45a-c27bf5a4e95a": "Gifting & Sales",
    "f0259d5e-3497-46b3-99a8-72dcab275170": "Returns & Care",
    "a9c50d1d-adb8-4f66-bb89-20f175d638f9": "Store & Escalations",
  },
  seedContext: { customerName: "Customer", items: [] },
  activities: ["Identify then verify the caller", "Recover a delayed order", "Start a return or gift", "Warm-transfer the store"],
  sampleUtterances: [
    "I got most of my order but the pyjama set still hasn't turned up.",
    "The bra I got has a broken clasp and the app won't let me return it.",
    "I need a birthday gift for my sister by Saturday.",
    "My order says delivered but I can't find it anywhere.",
  ],
  queries: [
    {
      id: "wismo",
      label: "Orders & Recovery",
      description: "Partial deliveries, delays and delivered-not-received — with pickup, overnight or a substitute, and a live store transfer to confirm stock.",
      sampleUtterances: [
        "I got most of my order but the pyjama set still hasn't turned up — I need it Friday.",
        "My order says delivered but I can't find it anywhere.",
        "Can I pick it up from a store today instead of waiting?",
        "Is the pink one actually in stock near me?",
      ],
    },
    {
      id: "refund-status",
      label: "Returns & Care",
      description: "Defective items, assisted returns when the app fails, refund timelines and exchanges.",
      sampleUtterances: [
        "The bra I got has a broken clasp and the app won't let me return it.",
        "Can I drop the return at a store, or post it?",
        "When do I actually get my money back?",
        "You sent me the wrong size.",
      ],
    },
    {
      id: "product-rec",
      label: "Gifting & Sales",
      description: "Gifting concierge — recommendations with reasoning, stock and delivery checks, gift wrap and a card message.",
      sampleUtterances: [
        "I need a birthday gift for my sister by Saturday, light floral, under a hundred.",
        "I'm not sure what she likes.",
        "Can you gift wrap it and hide the price?",
        "Will it arrive in time?",
      ],
    },
    {
      id: "cancellation",
      label: "Cancellations",
      description: "Cancellation requests checked against live order state — refused honestly once fulfilment has started, with the next valid path.",
      sampleUtterances: [
        "I placed an order this morning and I need to cancel it.",
        "I ordered it four hours ago — can't you make an exception?",
        "Can you change the delivery address instead?",
        "So what are my options?",
      ],
    },
  ],
};

export const AGENTS: AgentConfig[] = [
  ORDER_TRACKING_AGENT,
  COPA_CREW_AGENT,
  TSC_CREW_AGENT,
  TSC_MIKE_AGENT,
  TSC_MULTILINGUAL_AGENT,
  KNITWELL_AGENT,
  FINTECH_AGENT,
  FASHION_CREW_AGENT,
  NOVA_CREW_AGENT,
  VS_CREW_AGENT,
];

export function getAgentBySlug(slug: string): AgentConfig | undefined {
  return AGENTS.find((a) => a.slug === slug);
}
